import { Button, IconButton } from '@chakra-ui/button';
import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import { Icon } from '@chakra-ui/icon';
import { Flex, Box, Text, Heading, Stack, Badge } from '@chakra-ui/layout';
import { Skeleton } from '@chakra-ui/skeleton';
import { DotsIcons, MainButton } from './UiElements';
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverCloseButton,
    PopoverAnchor,
    Editable,
    EditablePreview,
    EditableInput,
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import layoutStore from '../stores/layoutStore';
import useStore from '../stores/store';
import { Portal } from '@chakra-ui/portal';
import { isTrialReached } from './userApi';
import { SignUpBody } from './AuthModal';
import { signOut } from 'firebase/auth';

const UserPopover = () => {
    const popoverShadow = useColorModeValue(
        '0 8px 22px -2px rgba(166,166,167,0.52), 0 16px 36px -4px rgba(166,166,167,0.32)',
        '0 8px 22px -2px rgba(19,20,24,0.95), , 0 16px 36px -4px rgba(19,20,24,0.75)'
    );
    const modalBg = useColorModeValue('#FFF', '#262A2D');
    const [popOpen, setPopOpen] = useState(false);
    const onPopClose = () => {
        setPopOpen(false);
    };
    const userState = useStore((state) => state.userData);
    const userStatus = useStore((state) => state.userStatus);
    const showLetter = userState?.name ? true : false;
    const letterToShow = userState?.name?.charAt(0).toUpperCase();

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const openDeleteModal = () => {
        setDeleteModalOpen(true);
    };
    const closeDeleteModal = () => {
        setDeleteModalOpen(false);
    };

    const signOutMiddle = () => {
        useStore.getState().signOut();
        setDeleteModalOpen(false);
        onPopClose();
    };

    return (
        <>
            <Popover
                placement="bottom-end"
                isOpen={popOpen}
                onClose={onPopClose}
                isLazy
            >
                <PopoverTrigger>
                    <div>
                        <MainButton
                            isLoading={userStatus === 'loading'}
                            icon={showLetter ? 'name' : 'user'} //'user'
                            letterToShow={letterToShow}
                            onClick={() => setPopOpen(true)}
                            // onClick={() => console.log('User popover')}
                        />
                    </div>
                </PopoverTrigger>
                {popOpen && (
                    <Portal>
                        <>
                            <div
                                onClick={onPopClose}
                                style={{
                                    backdropFilter: 'blur(1px)',
                                    background: 'rgba(0,0,0,0.16)',
                                    position: 'fixed',
                                    left: 0,
                                    right: 0,
                                    top: 0,
                                    bottom: 0,
                                    height: '100vh',
                                    width: '100vw',
                                    cursor: 'pointer',
                                    zIndex: 9
                                }}
                            />
                            <PopoverContent
                                zIndex={102}
                                bg={modalBg}
                                rounded="18px"
                                border="none"
                                boxShadow={popoverShadow}
                                w="348px"
                                //motionProps={{ transition: { duration: 0.05 } }}
                                motionProps={{
                                    initial: { y: -8 },
                                    animate: {
                                        y: 0,
                                        transition: { duration: 0.12 }
                                    },
                                    exit: {
                                        y: -8,
                                        transition: { duration: 0.1 }
                                    }
                                }}
                                style={{ zIndex: 102 }}
                            >
                                <PopoverCloseButton
                                    m="8px 4px"
                                    opacity="0.5"
                                    rounded="8px"
                                    // onClick={closePopover}
                                />
                                <UserPopoverHeader />
                                <UserBodyContent
                                    signOutMiddle={signOutMiddle}
                                    openDeleteModal={openDeleteModal}
                                />
                            </PopoverContent>
                        </>
                    </Portal>
                )}
            </Popover>
            {deleteModalOpen && (
                <AreYouSureLoggout
                    isOpen={true}
                    onClose={closeDeleteModal}
                    signOut={signOutMiddle}
                />
            )}
        </>
    );
};

export default UserPopover;

const UserPopoverHeader = () => {
    const userState = useStore((state) => state.userData);
    const userStatus = useStore((state) => state.userStatus);
    const trialLimitReached = useStore((state) => state.trialLimitReached);
    const trialSavesLeft = useStore((state) => state.trialSavesLeft);

    const loggedIn = userStatus === 'signedIn';

    const text = useColorModeValue('gray.900', 'white');
    const iconColor = useColorModeValue('gray.600', 'gray.200');
    const subTitle = useColorModeValue('gray.600', 'gray.300');
    const circleBorder = useColorModeValue('gray.300', 'gray.600');
    const hoverButton = useColorModeValue('gray.100', 'gray.700');
    const title = useColorModeValue('gray.900', 'white');
    const untitledColor = useColorModeValue('gray.300', 'gray.500');
    const iconBg = useColorModeValue(
        'linear-gradient(180deg, #F7F9FA 0%, #F1F4F8 54%, #E4EAF2 100%)',
        '#434A50'
    );
    const letterColor = useColorModeValue('gray.700', 'gray.200');
    const red = useColorModeValue('red.700', 'red.200');

    const [currentPlan, setCurrentPlan] = useState('');
    useEffect(() => {
        if (userState.stripeSubscriptionStatus === 'active') {
            setCurrentPlan('active');
        } else if (userState.stripeSubscriptionStatus === 'paused') {
            setCurrentPlan('paused');
        } else {
            if (trialLimitReached) {
                setCurrentPlan('trialLimitReached');
            } else if (userState.tour) {
                setCurrentPlan('tour');
            } else {
                setCurrentPlan('free');
            }
        }
    }, [userState, trialLimitReached]);

    const [newName, setName] = useState(userState.name);
    const handleChange = (event) => {
        const name = event.target.value.slice(0, 16);
        setName(name);
    };
    const submitName = (name) => {
        if (name !== userState.name) {
            useStore.getState().updateUserData({ name: name });
        }
    };

    const COUNT_OR_PLAN_TEXT = {
        tour: (
            <Text color={subTitle} fontSize="14px" cursor="default">
                <b>{trialSavesLeft}</b> saves left on trial plan
            </Text>
        ),
        free: (
            <Text color={subTitle} fontSize="14px" cursor="default">
                <b>{trialSavesLeft}</b> saves left on trial plan
            </Text>
        ),
        trialLimitReached: (
            <Text color={red} fontSize="14px" cursor="default">
                <b>{trialSavesLeft}</b> saves left on trial plan
            </Text>
        ),
        active: (
            <Text color={subTitle} fontSize="14px" cursor="default">
                Pro plan
            </Text>
        ),
        paused: (
            <Text color={subTitle} fontSize="14px" cursor="default">
                Pro plan{' '}
                <Badge mt="-3px" colorScheme="orange" size="xs" rounded="6px">
                    {userState.stripeSubscriptionStatus}
                </Badge>
            </Text>
        )
    };

    return (
        <Flex px="20px" pt="20px">
            <Flex
                bg={iconBg}
                rounded="16px"
                height="58px"
                minWidth="58px"
                marginTop="4px"
                p="0px"
                alignItems="center"
                border="var(--chakra-colors-teBorder)"
            >
                <Text
                    color={letterColor}
                    fontSize="24px"
                    fontWeight={'600'}
                    m="auto"
                >
                    {userState.name !== '' ? (
                        userState.name.charAt(0).toUpperCase()
                    ) : (
                        <Icon
                            name="userAccount"
                            color={letterColor}
                            as={UserIcon}
                            mt="-4px"
                        />
                    )}
                </Text>
            </Flex>
            <Flex flexDir="column" marginLeft="16px" maxWidth="78%" mt="2px">
                {loggedIn ? (
                    <Editable
                        my="4px"
                        py="0"
                        mr="24px"
                        value={newName}
                        startWithEditView={userState.name !== '' ? false : true}
                        placeholder="Enter username"
                        fontSize="20px"
                        fontWeight="600"
                        color={userState.name !== '' ? title : untitledColor}
                        onSubmit={() => {
                            submitName(newName);
                        }}
                    >
                        <EditablePreview
                            py="0"
                            _hover={{
                                bg: hoverButton,
                                color:
                                    userState.name === '' ? 'gray.400' : title
                            }}
                        />
                        <EditableInput
                            py="0"
                            color={title}
                            value={newName}
                            onChange={handleChange}
                        />
                    </Editable>
                ) : (
                    <Text
                        fontSize="22px"
                        fontWeight="700"
                        color={text}
                        cursor="default"
                    >
                        -
                    </Text>
                )}
                <Box
                    mt="-2px"
                    fontSize="14px"
                    fontWeight="500"
                    color={text}
                    cursor="default"
                >
                    {COUNT_OR_PLAN_TEXT[currentPlan]}
                </Box>
            </Flex>
        </Flex>
    );
};

const UserBodyContent = ({ signOutMiddle, openDeleteModal }) => {
    const buttonProps = {
        transition: '0.1s',
        rounded: '8px',
        fontSize: '14px'
    };
    const userData = useStore((state) => state.userData);

    const [isLoading, setIsLoaded] = useState(null);

    const isGuest =
        userData?.email === 'Guest' ||
        (userData && userData.email !== undefined && userData.email === '');

    return (
        <Stack
            px="20px"
            pb="16px"
            spacing="8px"
            mt="16px"
            borderTop="var(--chakra-colors-teBorder)"
        >
            {!isGuest ? (
                <>
                    <Flex mb="28px" mt="8px">
                        <EmailIcon />
                        <Text fontSize="14px" ml="8px">
                            {userData?.email || ''}
                        </Text>
                    </Flex>
                    <Button
                        {...buttonProps}
                        onClick={(e) => {
                            e.preventDefault();
                            window.location.href =
                                'https://www.tabextend.com/settings/general';
                        }}
                    >
                        <EditIcon />
                        Manage account
                    </Button>
                    <Button onClick={() => signOutMiddle()} {...buttonProps}>
                        <SignOutIcon />
                        Sign out
                    </Button>
                </>
            ) : (
                <Stack>
                    <SignUpBody
                        setIsLoaded={setIsLoaded}
                        isLoading={isLoading}
                        isAlreadyGuest={true}
                    />
                    <Button {...buttonProps} onClick={openDeleteModal}>
                        <SignOutIcon />
                        Sign out
                    </Button>
                </Stack>
            )}
        </Stack>
    );
};

const AreYouSureLoggout = ({ isOpen, onClose, signOut }) => {
    const modalBg = useColorModeValue('#FFF', '#262A2D');
    const cancelRef = useRef();

    return (
        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
        >
            <AlertDialogOverlay>
                <AlertDialogContent rounded="12px" bg={modalBg}>
                    <AlertDialogHeader
                        fontSize="lg"
                        fontWeight="bold"
                        pb="4px"
                        mt="12px"
                        display="flex"
                        alignContent="center"
                        paddingRight="44px"
                    >
                        <Flex
                            p="4px"
                            mr="16px"
                            bg="#ff4b4a30"
                            rounded="10px"
                            alignContent="center"
                            alignItems="center"
                            minW="54px"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                viewBox="0 0 20 20"
                                fill="#ff4b4a"
                                style={{
                                    width: '32px',
                                    height: '32px',
                                    margin: 'auto'
                                }}
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </Flex>
                        Warning: By signing out, your current saved data will be
                        lost
                    </AlertDialogHeader>
                    <AlertDialogBody fontSize={'14px'}>
                        Since you are on a <b>guest account</b>, all data will
                        be lost if you sign out. To keep your data, stay signed
                        in or link it with an email/password or Google.
                    </AlertDialogBody>
                    <AlertDialogFooter px="16px">
                        <Button ref={cancelRef} onClick={onClose} rounded="8px">
                            Cancel
                        </Button>
                        <Button
                            bg={'#e53b3a'}
                            onClick={signOut}
                            ml={3}
                            rounded="8px"
                            transition={'0.1s'}
                            _hover={{ bg: '#c53131' }}
                            color={'#FFF'}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="#FFF"
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    margin: 'auto 8px auto 0px'
                                }}
                            >
                                <path
                                    d="M9 2C8.62123 2 8.27497 2.214 8.10557 2.55279L7.38197 4H4C3.44772 4 3 4.44772 3 5C3 5.55228 3.44772 6 4 6L4 16C4 17.1046 4.89543 18 6 18H14C15.1046 18 16 17.1046 16 16V6C16.5523 6 17 5.55228 17 5C17 4.44772 16.5523 4 16 4H12.618L11.8944 2.55279C11.725 2.214 11.3788 2 11 2H9ZM7 8C7 7.44772 7.44772 7 8 7C8.55228 7 9 7.44772 9 8V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V8ZM12 7C11.4477 7 11 7.44772 11 8V14C11 14.5523 11.4477 15 12 15C12.5523 15 13 14.5523 13 14V8C13 7.44772 12.5523 7 12 7Z"
                                    fillRule="evenodd"
                                />
                            </svg>
                            Yes, sign me out
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
};

const EmailIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '16px' }}
        >
            <path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67Z" />
            <path d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908Z" />
        </svg>
    );
};

const SignOutIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            style={{ width: '16px', marginRight: '8px' }}
        >
            <path
                fillRule="evenodd"
                d="M3 4.25A2.25 2.25 0 0 1 5.25 2h5.5A2.25 2.25 0 0 1 13 4.25v2a.75.75 0 0 1-1.5 0v-2a.75.75 0 0 0-.75-.75h-5.5a.75.75 0 0 0-.75.75v11.5c0 .414.336.75.75.75h5.5a.75.75 0 0 0 .75-.75v-2a.75.75 0 0 1 1.5 0v2A2.25 2.25 0 0 1 10.75 18h-5.5A2.25 2.25 0 0 1 3 15.75V4.25Z"
                clipRule="evenodd"
            />
            <path
                fillRule="evenodd"
                d="M6 10a.75.75 0 0 1 .75-.75h9.546l-1.048-.943a.75.75 0 1 1 1.004-1.114l2.5 2.25a.75.75 0 0 1 0 1.114l-2.5 2.25a.75.75 0 1 1-1.004-1.114l1.048-.943H6.75A.75.75 0 0 1 6 10Z"
                clipRule="evenodd"
            />
        </svg>
    );
};

const EditIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            style={{ width: '14px', marginRight: '8px' }}
        >
            <path d="m2.695 14.762-1.262 3.155a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.886L17.5 5.501a2.121 2.121 0 0 0-3-3L3.58 13.419a4 4 0 0 0-.885 1.343Z" />
        </svg>
    );
};

export const UserIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 22"
            fill="var(--chakra-colors-iconMd)"
            style={{ width: '28px' }}
        >
            <g>
                <circle id="Oval" cx="10" cy="5" r="5"></circle>
                <path
                    d="M9.70820393,13 L10,13 L10,13 L10.2917961,13 C14.4030266,13 18.1614018,15.3228036 20,19 L20,19 L20,19 C18.7505107,20.8742339 16.6469976,22 14.3944487,22 L5.60555128,22 C3.35300242,22 1.24948929,20.8742339 0,19 L0,19 L0,19 C1.83859819,15.3228036 5.59697339,13 9.70820393,13 Z"
                    id="Rectangle"
                ></path>
            </g>
        </svg>
    );
};
